import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { useAuth } from "@/lib/auth";

const TRIAL_REMINDER_EVENT = "tales:trial-reminder";

export function showTrialReminder(reason = "play") {
  window.dispatchEvent(new CustomEvent(TRIAL_REMINDER_EVENT, { detail: { reason } }));
}

const copyFor = {
  login: {
    title: "Your Mage trial is waiting",
    body: "Try two days of richer prose, stronger continuity, and story-aware choices.",
  },
  play: {
    title: "Try a premium narrator on this saga",
    body: "Your free Mage trial adds deeper continuity and more story-aware choices.",
  },
};

export default function TrialReminder() {
  const { user } = useAuth();
  const [reason, setReason] = useState(null);
  const timerRef = useRef(null);

  const eligible = Boolean(user) && !user.trial_used && !user.trial_started_at;

  const dismiss = () => {
    window.clearTimeout(timerRef.current);
    setReason(null);
  };

  const present = (nextReason) => {
    if (!eligible) return;
    window.clearTimeout(timerRef.current);
    setReason(nextReason);
    timerRef.current = window.setTimeout(dismiss, 10000);
  };

  useEffect(() => {
    const onReminder = (event) => present(event.detail?.reason || "play");
    window.addEventListener(TRIAL_REMINDER_EVENT, onReminder);
    return () => {
      window.removeEventListener(TRIAL_REMINDER_EVENT, onReminder);
      window.clearTimeout(timerRef.current);
    };
  }, [eligible]);

  useEffect(() => {
    if (!eligible) dismiss();
  }, [eligible]);

  if (!eligible || !reason) return null;

  const copy = copyFor[reason] || copyFor.play;

  return (
    <aside
      className="fixed inset-x-0 bottom-0 z-[80] border-t border-amber-500/60 bg-[#15121a]/[0.98] shadow-[0_-18px_42px_rgba(0,0,0,0.55)] sm:inset-x-auto sm:bottom-5 sm:right-5 sm:w-[380px] sm:border sm:shadow-2xl"
      role="status"
      aria-live="polite"
      data-testid="trial-reminder"
    >
      <div className="mx-auto max-w-xl px-5 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 sm:max-w-none sm:p-5">
        <div className="flex items-start gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center border border-amber-500/55 bg-amber-950/30 text-amber-300">
            <Sparkles className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-display text-lg leading-6 text-amber-100">{copy.title}</p>
            <p className="mt-1 text-sm leading-5 text-amber-100/70">{copy.body}</p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="grid h-9 w-9 shrink-0 place-items-center text-amber-100/65 transition hover:bg-amber-900/25 hover:text-amber-100"
            aria-label="Dismiss trial reminder"
            title="Dismiss"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={dismiss}
            className="min-h-11 px-3 text-sm text-amber-100/75 transition hover:text-amber-100"
          >
            Not now
          </button>
          <Link
            to="/pricing"
            onClick={dismiss}
            className="inline-flex min-h-11 items-center justify-center border border-amber-400 bg-amber-500 px-4 text-sm font-semibold text-stone-950 transition hover:bg-amber-400"
          >
            Start 2-day trial
          </Link>
        </div>
      </div>
    </aside>
  );
}
